import { useCallback, useEffect, useRef } from 'react';
import { ChatWindow } from './components/chat/ChatWindow';
import { QuestionBar } from './components/QuestionBar';
import { useChatState } from './hooks/useChatState';
import { useUserState } from './hooks/useUserState';
import { useWorkerConnection } from './hooks/useWorkerConnection';
import { useHistory } from './hooks/useHistory';
import { Message } from './types';

function App() {
  // Custom hooks
  const chatState = useChatState();
  const userState = useUserState();
  const history = useHistory();
  const turnstileTokenRef = useRef<string | null>(null);
  const sessionInitializedRef = useRef(false);
  const historyLoadedRef = useRef(false);

  // Handle SSE messages for real-time updates via useWorkerConnection callback
  const handleSSEMessage = useCallback(
    (message: Message) => {
      chatState.setMessages((prev) => {
        // 1. Check if the message already exists by ID
        const index = prev.findIndex((m) => m.id === message.id);
        if (index !== -1) {
          // If the text is the same, do nothing
          if (prev[index].text === message.text) return prev;

          console.log('[App] Updating existing message:', message.id);
          const updated = [...prev];
          updated[index] = { ...updated[index], ...message };
          return updated;
        }

        // 2. Check if this is a user message matching an optimistic placeholder
        // We look for a message from the same user (by email) with the same text and a temporary ID
        if (message.email && message.email === userState.email) {
          const optimisticIndex = prev.findIndex((m) => m.id.startsWith('msg-') && m.text === message.text);
          if (optimisticIndex !== -1) {
            console.log(
              '[App] SSE message matches optimistic user message, replacing:',
              prev[optimisticIndex].id,
              '->',
              message.id
            );
            const updated = [...prev];
            updated[optimisticIndex] = message;
            return updated;
          }
        }

        console.log('[App] Adding new SSE message:', message.id);
        return [...prev, message];
      });

      // Increment unread if minimized
      if (chatState.isChatMinimized) {
        chatState.incrementUnreadCount();
      }
    },
    [chatState, userState.email]
  );

  // Handle session creation - store in localStorage
  const handleSessionCreated = useCallback(
    (newSessionId: string) => {
      userState.setSessionId(newSessionId);
    },
    [userState]
  );

  // Create worker connection with SSE message handler
  // Pass stored session ID and callback for when new session is created
  const workerConnection = useWorkerConnection(
    handleSSEMessage,
    chatState.isChatOpen,
    userState.sessionId || undefined,
    handleSessionCreated
  );

  // Initialize session when component mounts
  useEffect(() => {
    if (!sessionInitializedRef.current) {
      sessionInitializedRef.current = true;
      workerConnection.initializeSession();
    }
  }, [workerConnection.initializeSession]);

  // Load message history when user email is available
  useEffect(() => {
    const loadUserHistory = async () => {
      if (!userState.email || historyLoadedRef.current) {
        return;
      }

      console.log('[App] Loading history for email:', userState.email);
      historyLoadedRef.current = true;

      const messages = await history.loadHistory(userState.email);

      if (messages.length > 0) {
        console.log('[App] Loaded', messages.length, 'messages from history');
        chatState.setMessages(messages);

        // Returning user with history - mark session as trusted (skip Turnstile)
        // This is safe because they've already verified in a previous session
        if (workerConnection.sessionId) {
          console.log('[App] Marking returning user session as verified');
          try {
            await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:8787'}/api/turnstile/verify`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                session_id: workerConnection.sessionId,
                token: 'RETURNING_USER_AUTO_VERIFIED', // Special token for returning users
              }),
            });
          } catch (err) {
            console.warn('[App] Failed to auto-verify returning user:', err);
          }
        }

        // If user has history, open chat in minimized state
        // This shows the chat window in the corner instead of the round button
        chatState.openChat();
        chatState.minimizeChat();
      }
    };

    loadUserHistory();
  }, [userState.email, history, chatState, workerConnection.sessionId]);

  // Handle sending messages
  const handleSendMessage = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!chatState.newMessage.trim() || chatState.isLoading || !workerConnection.sessionId) return;

      const messageText = chatState.newMessage.trim();

      // Create optimistic message
      const newMsg: Message = {
        id: `msg-${Date.now()}`,
        text: messageText,
        sender: userState.username,
        email: userState.email,
        timestamp: new Date().toISOString(),
        isFromSlack: false,
      };

      // Add message to list optimistically
      chatState.setMessages((prev) => [...prev, newMsg]);
      chatState.setNewMessage('');
      chatState.setIsLoading(true);

      try {
        // Send message to Worker
        const sentInfo = await workerConnection.sendMessage(
          messageText,
          userState.email,
          userState.username,
          turnstileTokenRef.current || undefined
        );

        if (!sentInfo) {
          // Handle error - message already shown via workerConnection.error
          chatState.setError(workerConnection.error || 'Failed to send message');
          // Remove optimistic message
          chatState.setMessages((prev) => prev.filter((m) => m.id !== newMsg.id));
        } else {
          const { userMessage, assistantMessage } = sentInfo;
          // Replace optimistic message with server response (including real server ID)
          // This ensures SSE deduplication works correctly
          console.log('[App] Replacing optimistic message:', newMsg.id, '-> server message:', userMessage.id);
          chatState.setMessages((prev) => {
            // Check if the server's version of the user message was already added (e.g., via SSE)
            const userMsgExists = prev.some((m) => m.id === userMessage.id);

            let updated;
            if (userMsgExists) {
              // If it already exists, just remove the optimistic placeholder
              console.log('[App] User message already exists, removing placeholder:', newMsg.id);
              updated = prev.filter((m) => m.id !== newMsg.id);
            } else {
              // Otherwise, replace the placeholder with the real message
              console.log('[App] Replacing optimistic message:', newMsg.id, '-> server message:', userMessage.id);
              updated = prev.map((m) => (m.id === newMsg.id ? userMessage : m));
            }

            // Add assistant message if provided
            if (assistantMessage) {
              if (!updated.some((m) => m.id === assistantMessage.id)) {
                console.log('[App] Adding assistant message from response:', assistantMessage.id);
                updated = [...updated, assistantMessage];
              }
            }

            return updated;
          });

          // If fallback occurred, show a brief notification
          if (userMessage.isFallback && userMessage.fallback_reason) {
            // The notification message will come via SSE, but we can show a brief status
            // The actual notification message will be displayed when it arrives
          }

          // Clear Turnstile token after first use
          turnstileTokenRef.current = null;
        }
      } catch (err) {
        console.error('Send message error:', err);
        chatState.setError(err instanceof Error ? err.message : 'Failed to send message');
        // Remove optimistic message
        chatState.setMessages((prev) => prev.filter((m) => m.id !== newMsg.id));
      } finally {
        chatState.setIsLoading(false);
      }
    },
    [chatState, userState, workerConnection]
  );

  // Handle message input change
  const handleMessageChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      chatState.setNewMessage(e.target.value);
    },
    [chatState]
  );

  // Handle Turnstile verification
  const handleTurnstileVerify = useCallback(
    async (token: string) => {
      turnstileTokenRef.current = token;
      await workerConnection.verifyTurnstile(token);
    },
    [workerConnection]
  );

  // Handle user setup (name/email submission)
  const handleSetUsername = useCallback(
    async (username: string, email: string) => {
      userState.handleSetUser(username, email);

      // Check if there's a selected question to auto-send
      const questionToSend = chatState.selectedQuestion;

      if (questionToSend) {
        // Clear the selected question and input
        chatState.clearSelectedQuestion();

        // Create optimistic message for the question
        const newMsg: Message = {
          id: `msg-${Date.now()}`,
          text: questionToSend,
          sender: username,
          email: email,
          timestamp: new Date().toISOString(),
          isFromSlack: false,
        };

        chatState.setMessages([newMsg]);
        chatState.setIsLoading(true);

        try {
          // Send the question automatically
          const sentInfo = await workerConnection.sendMessage(
            questionToSend,
            email,
            username,
            turnstileTokenRef.current || undefined
          );

          if (sentInfo) {
            const { userMessage, assistantMessage } = sentInfo;
            // Replace optimistic message with server response (including real server ID)
            chatState.setMessages((prev) => {
              const userMsgExists = prev.some((m) => m.id === userMessage.id);
              let updated;
              if (userMsgExists) {
                updated = prev.filter((m) => m.id !== newMsg.id);
              } else {
                updated = prev.map((m) => (m.id === newMsg.id ? userMessage : m));
              }

              if (assistantMessage) {
                if (!updated.some((m) => m.id === assistantMessage.id)) {
                  updated = [...updated, assistantMessage];
                }
              }
              return updated;
            });
            turnstileTokenRef.current = null;
          } else {
            chatState.setError(workerConnection.error || 'Failed to send message');
            chatState.setMessages((prev) => prev.filter((m) => m.id !== newMsg.id));
          }
        } catch (err) {
          console.error('Auto-send message error:', err);
          chatState.setError(err instanceof Error ? err.message : 'Failed to send message');
          chatState.setMessages((prev) => prev.filter((m) => m.id !== newMsg.id));
        } finally {
          chatState.setIsLoading(false);
        }
      } else {
        // No selected question - show welcome message
        const welcomeMsg: Message = {
          id: `msg-welcome-${Date.now()}`,
          text: `Welcome ${username}! 👋 How can I help you today?`,
          sender: 'Studio Graphene',
          timestamp: new Date().toISOString(),
          isFromSlack: false,
        };
        chatState.setMessages([welcomeMsg]);
      }
    },
    [userState, chatState, workerConnection]
  );

  // Handle logout
  const handleLogout = useCallback(() => {
    // Reset history loaded flag
    historyLoadedRef.current = false;

    // Clear user data and messages
    userState.clearUserData();
    chatState.setMessages([]);
    chatState.closeChat();
    chatState.closeLogoutModal();
  }, [userState, chatState]);

  // Handle clear history (keep user logged in, just clear messages)
  const handleClearHistory = useCallback(async () => {
    if (!userState.email) {
      return;
    }

    const success = await history.clearHistory(userState.email);

    if (success) {
      // Clear messages from UI
      chatState.setMessages([]);
      chatState.setSuccessMessage('Chat history cleared');
      console.log('[App] History cleared for', userState.email);
    } else {
      chatState.setError(history.historyError || 'Failed to clear history');
    }
  }, [userState.email, history, chatState]);

  // Handle question click from QuestionBar
  const handleQuestionClick = useCallback(
    (question: string) => {
      chatState.handleQuestionClick(question);
    },
    [chatState]
  );

  return (
    <>
      {/* Question Bar - Sliding at bottom */}
      <QuestionBar onQuestionClick={handleQuestionClick} className="" isEmbedded={false} />

      {/* Floating Chat Button (when closed) */}
      {!chatState.isChatOpen && (
        <button
          onClick={chatState.openChat}
          className="fixed bottom-5 right-5 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-blue-600 text-white shadow-lg transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-blue-400"
          aria-label="Open chat"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className="h-6 w-6"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z"
            />
          </svg>
        </button>
      )}

      {/* Chat Window */}
      <ChatWindow
        ref={chatState.chatWindowRef}
        isOpen={chatState.isChatOpen}
        isMinimized={chatState.isChatMinimized}
        unreadMessagesCount={chatState.unreadMessagesCount}
        isEmbedded={false}
        username={userState.username}
        email={userState.email}
        isSettingUsername={userState.isSettingUsername}
        messages={chatState.messages}
        newMessage={chatState.newMessage}
        isLoading={chatState.isLoading}
        error={chatState.error}
        successMessage={chatState.successMessage}
        isLogoutModalOpen={chatState.isLogoutModalOpen}
        connectionStatus={workerConnection.connectionStatus}
        onClose={chatState.closeChat}
        onMinimize={chatState.minimizeChat}
        onRestore={chatState.restoreChat}
        onLogout={handleLogout}
        onClearHistory={handleClearHistory}
        onSendMessage={handleSendMessage}
        onMessageChange={handleMessageChange}
        onSetUsername={handleSetUsername}
        onCloseLogoutModal={chatState.closeLogoutModal}
        onTurnstileVerify={handleTurnstileVerify}
        validateMX={workerConnection.validateMX}
        onOpenLogoutModal={chatState.openLogoutModal}
        messageInputRef={chatState.messageInputRef}
        messagesEndRef={chatState.messagesEndRef}
      />
    </>
  );
}

export default App;
